import { getConfig, klinikenApi } from "@/api";
import moment from "moment/moment";
import store from "@/store";
import klinikenLogoSmall from "@/assets/kliniken-logo-small.svg";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PrintMeta {
    utskriftId: string;
    profileName: string;
    date: string;
}

interface PatientDisplay {
    namn?: string;
    personId_formatted?: string;
}

export interface PrintWindowOptions {
    /** Backend endpoint that registers the print and returns a utskrifts_id */
    url: string;
    /** The items whose ids are sent to the backend */
    items: { id: number | string }[];
    /** Human-readable name used in audit logs */
    logName: string;
    /** The DOM element whose rendered content should be printed */
    contentElement: HTMLElement;
    /** Optional patient info shown in the print header */
    patientInfo?: PatientDisplay;
    /** Page orientation – defaults to "portrait" */
    orientation?: "portrait" | "landscape";
    /** Document title shown in the browser tab and print dialog – defaults to "Utskrift" */
    title?: string;
    /** Optional print preset for form field layout */
    fieldDisplayLayout?: "default" | "columns";
}

/* ------------------------------------------------------------------ */
/*  Backend registration                                               */
/* ------------------------------------------------------------------ */

/**
 * Registers a print action with the backend and returns metadata used
 * in the print header (utskrifts-ID, date, user name).
 *
 * The backend endpoint creates an audit log entry so every print can
 * be traced back to a specific user, date and set of records.
 *
 * @param url      - The backend endpoint (e.g. "/operation/anmalan/utskrift/")
 * @param items    - Array of objects whose `id` fields are sent to the backend
 * @param logName  - Human-readable label used in the audit log (e.g. "Dagsöversikt")
 * @returns Metadata object with utskriftId, formatted date and user's full name
 * @throws If the backend response does not contain a valid `utskrifts_id`
 */
async function registerPrint(url: string, items: { id: number | string }[], logName: string): Promise<PrintMeta> {
    const { data } = await klinikenApi.post(url, { ids: items.map((i) => i.id) }, getConfig({}, logName));

    if (!data.utskrifts_id) {
        throw new Error("Id för utskriften saknas");
    }

    const currentProfile = store.getters["userData/currentProfile"];

    return {
        utskriftId: data.utskrifts_id,
        date: moment().format("YYYY-MM-DD"),
        profileName: currentProfile.user_display.name,
    };
}

/* ------------------------------------------------------------------ */
/*  DOM helpers                                                        */
/* ------------------------------------------------------------------ */

/**
 * Collects every stylesheet currently loaded in the main application
 * window and returns them as an HTML string of `<link>` and `<style>`
 * tags.
 *
 * `<link>` elements are emitted with their fully-resolved `href`
 * (absolute URL) so the print window can fetch them regardless of its
 * own document origin.
 *
 * Inline `<style>` elements are copied verbatim via `outerHTML`.
 *
 * The returned string is injected into the `<head>` of the print
 * document so the cloned content looks identical to the main app.
 */
function collectStylesheets(): string {
    const parts: string[] = [];

    document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
        const el = link as HTMLLinkElement;
        parts.push(`<link rel="stylesheet" href="${el.href}">`);
    });

    document.querySelectorAll("style").forEach((style) => {
        parts.push(style.outerHTML);
    });

    return parts.join("\n");
}

/**
 * Strips interactive / non-printable elements from a cloned DOM tree
 * so the printed output only contains static, readable content.
 *
 * Transformations applied:
 *  - Buttons are removed entirely.
 *  - Action bars (`.form-actions`, `.button-group`) are removed.
 *  - `<textarea>` elements are replaced with `<span>` containing their
 *    current `.value` (preserving whitespace).
 *  - Checkboxes are replaced with a ✓ / ✗ text representation.
 *
 * @param el - The root element of the cloned DOM subtree to clean.
 *             The element is mutated in place.
 */
function cleanForPrint(el: HTMLElement): void {
    el.querySelectorAll("button").forEach((b) => b.remove());

    el.querySelectorAll(".form-actions, .button-group").forEach((b) => b.remove());

    el.querySelectorAll("textarea").forEach((ta) => {
        const span = document.createElement("span");
        span.textContent = (ta as HTMLTextAreaElement).value;
        span.style.whiteSpace = "pre-wrap";
        ta.replaceWith(span);
    });

    el.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        const span = document.createElement("span");
        span.textContent = (cb as HTMLInputElement).checked ? "✓" : "✗";
        cb.replaceWith(span);
    });
}

/* ------------------------------------------------------------------ */
/*  HTML helpers                                                       */
/* ------------------------------------------------------------------ */

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

/* ------------------------------------------------------------------ */
/*  HTML builders                                                      */
/* ------------------------------------------------------------------ */

/**
 * Builds the inner HTML for the print header, matching the layout of
 * the old `PrintHeader.vue` component:
 *
 *  ┌─────────────────────────────────────────────────────────┐
 *  │  Utskriftsdatum: …   Utskrivet av: …   ID: …   [logo]   │
 *  │  Patient: Namn Personnummer           (if provided)     │
 *  └─────────────────────────────────────────────────────────┘
 *
 * The top row uses a CSS grid with print-info on the left and the
 * "Kliniken Patientsystem" branding + small logo on the right.
 *
 * The optional bottom row (patient info) is bold, full-width, and
 * separated by a 1px black border-bottom.
 *
 * @param meta        - Print metadata (date, user name, utskrifts-ID)
 * @param patientInfo - Optional patient name and formatted personnummer
 * @returns Raw HTML string (not a full document, just the header content)
 */
function buildHeaderContent(meta: PrintMeta, patientInfo?: PatientDisplay): string {
    const bottomRow =
        patientInfo?.namn && patientInfo?.personId_formatted
            ? `<div class="ph-bottom">
                   <span>Patient: ${escapeHtml(patientInfo.namn)} ${escapeHtml(patientInfo.personId_formatted)}</span>
               </div>`
            : "";

    return `
        <div class="ph-top">
            <div class="ph-print-info">
                <span>Utskriftsdatum: ${escapeHtml(meta.date)}</span>
                <span>Utskrivet av: ${escapeHtml(meta.profileName)}</span>
                <span>ID: ${escapeHtml(meta.utskriftId)}</span>
            </div>
            <span class="ph-kliniken-logo">Kliniken Patientsystem <img src="${klinikenLogoSmall}" alt="" /></span>
        </div>
        ${bottomRow}`;
}

/**
 * Assembles a complete, self-contained HTML document for the print
 * window.
 *
 * ## Repeating-header strategy (hybrid: fixed overlay + thead spacer)
 *
 * Getting a header on every printed page *without overlap* requires
 * solving two independent problems:
 *
 * ### A — Rendering the header on every page
 * A `position: fixed` div (`#print-header-overlay`) is hidden on
 * screen (`display:none`) and shown during print.  Chrome renders
 * fixed-position elements at the same coordinates on every printed
 * page, so the header appears consistently.  It carries `z-index:9999`
 * and a white background to paint cleanly on top of anything beneath.
 *
 * ### B — Reserving vertical space so body content is never behind it
 * A `<table>` with a `<thead>` that contains the **same** header
 * markup plus a 1.2 rem gap spacer.  Because the content is identical
 * the browser calculates exactly the right height automatically — no
 * JavaScript measurement needed.  Browsers repeat `<thead>` on every
 * page, which pushes the `<tbody>` content down below the overlay.
 *
 * ### Print-specific CSS highlights
 * - `break-inside: auto` and `overflow: visible` on all content
 *   descendants to allow natural page breaking.
 * - All table-structure elements carry inline `style="…!important"` so
 *   copied app stylesheets can never override the layout.
 *
 * @param headerHtml         - HTML fragment produced by `buildHeaderContent()`
 * @param contentHtml        - `outerHTML` of the cloned + cleaned content element
 * @param stylesheets        - `<link>` / `<style>` tags from `collectStylesheets()`
 * @param orientation        - `"portrait"` or `"landscape"` for `@page { size }`
 * @param title              - Document title shown in the browser tab and print dialog
 * @param fieldDisplayLayout - Optional preset for form field layout (default or columns)
 * @returns A complete `<!DOCTYPE html>…</html>` string ready for
 *          `document.write()` in the print window
 */
function buildDocument(
    headerHtml: string,
    contentHtml: string,
    stylesheets: string,
    orientation: string,
    title: string,
    fieldDisplayLayout: PrintWindowOptions["fieldDisplayLayout"]
): string {
    /*
     * Repeating-header approach  (hybrid: fixed overlay + thead spacer)
     * ─────────────────────────────────────────────────────────────────
     *
     *   A. RENDERING the header on every page:
     *      A `position: fixed` div (`#print-header-overlay`) that Chrome
     *      renders at the same position on every printed page.
     *      It has z-index + white background so it paints cleanly on top.
     *      Hidden on screen (`display:none`) – the thead already shows it.
     *
     *   B. RESERVING SPACE so body content doesn't start behind it:
     *      The `<thead>` contains the **same** header HTML plus a gap
     *      spacer.  Because the content is identical the reserved height
     *      is always exactly right – no JavaScript measurement needed.
     *      The browser repeats `<thead>` on every page, pushing the
     *      `<tbody>` content down.
     *
     *   The fixed overlay sits on top of the thead content with a white
     *   background, so even if Chrome's thead-overlap bug slightly
     *   mispositions the tbody, the overlay hides the mess.
     */

    // Shared inline styles for the header "box" (used in both places)
    // Matches old PrintHeader.vue: font-size 0.7rem, color $color-text-popup (#55676d)
    const headerBoxStyle = `
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        font-size: 0.7rem;
        color: #55676d;
    `
        .replace(/\n\s*/g, " ")
        .trim();

    const fieldDisplayLayoutCss =
        fieldDisplayLayout === "columns"
            ? `
/*
 * Optional preset: render each displayed form field as two columns
 * (label left, value right) for compact, report-like printouts.
 */
#print-body .form-content,
#print-body .form-section {
    display: block !important;
}

#print-body .form-content > *,
#print-body .form-section > * {
    grid-column: 1 / -1 !important;
}

#print-body .k-form-field-display {
    display: grid !important;
    grid-template-columns: minmax(10rem, 25%) 1fr;
    grid-template-rows: auto;
    column-gap: 0.75rem;
    align-items: start;
    width: 100% !important;
    margin-bottom: 0.25rem;
}

#print-body .k-form-field-display .k-label {
    max-width: none !important;
    margin-bottom: 0 !important;
}

#print-body .k-form-field-display .content {
    display: block !important;
    min-width: 0;
}

#print-body .k-form-field-display .content > span {
    display: block;
}
`
            : "";

    return `<!DOCTYPE html>
<html lang="sv">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
${stylesheets}
<style>
/*
 * ================================================================
 *  PAGE SETUP
 * ================================================================
 *  Configures the physical page dimensions and margins for the
 *  browser's print engine.
 *
 *  - size: A4 in the requested orientation (portrait / landscape).
 *  - margin: 20 mm top, 12 mm left/right, 15 mm bottom.
 *    These define the "content area" on each printed page — all
 *    normal-flow content is confined within these margins.
 */
@page {
    size: ${orientation === "landscape" ? "297mm 210mm" : "210mm 297mm"};
    margin: 15mm 10mm 12mm 10mm;
}

/*
 * Use border-box globally so that padding and border are included
 * in element widths/heights.  This prevents unexpected overflow
 * when app styles assume border-box (which they do via Vite's
 * base CSS).
 */
*, *::before, *::after { box-sizing: border-box; }

/*
 * Base document styles.
 *  - Reset browser defaults (margin/padding).
 *  - White background + black text for clean printing.
 *  - print-color-adjust: economy — lets users control whether backgrounds
 *    are printed via the browser's "Background graphics" checkbox in the
 *    print dialog.  Use "exact" to always force backgrounds.
 *  - 0.8 rem base font with line-height 1.3 keeps body text readable on
 *    A4 while compressing vertical space compared to screen defaults.
 */
html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #000;
    -webkit-print-color-adjust: economy;
    print-color-adjust: economy;
    font-size: 0.9rem;
    line-height: 1.3;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

/*
 * ================================================================
 *  HEADER INNER STYLES
 * ================================================================
 *  These match the old PrintHeader.vue component's scoped CSS.
 *  The header is rendered twice (overlay + thead spacer) with the
 *  same markup, so these class-based rules style both copies.
 */

/*
 * .ph-top — The top row of the header.
 *  Two-column grid:
 *    Left column (1fr):  print-info (date, user, ID)
 *    Right column (max-content): "Kliniken Patientsystem" + logo
 *  padding-bottom adds a small gap before the optional patient row.
 */
.ph-top {
    display: grid;
    align-items: center;
    gap: 0.5rem;
    grid-template-columns: 1fr max-content;
    grid-auto-flow: column;
    padding-bottom: 0.3rem;
}

/*
 * .ph-print-info — Date / user / ID metadata.
 *  Flex-wrap so items flow to a second line on narrow pages
 *  (e.g. landscape with patient info).
 *  font-size: 0.85rem — slightly larger than the container so the
 *  metadata is clearly readable, but smaller than before to save space.
 */
.ph-print-info {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    font-size: 0.85rem;
}

/*
 * .ph-kliniken-logo — Branding block on the right.
 *  Inline grid places the text and the small SVG icon side by side.
 *  margin-left: auto pushes it to the far right of the grid cell.
 */
.ph-kliniken-logo {
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    margin-left: auto;
    grid-gap: 0.5rem;
    font-weight: 600;
}
.ph-kliniken-logo img {
    height: 24px;
    width: 24px;
}

/*
 * .ph-bottom — Optional patient info row.
 *  Only rendered when patientInfo is provided.
 *  Separated from the top row by a 1px black bottom border.
 *  font-size: 0.9rem and color: #000 override the header container's
 *  smaller/lighter defaults so the patient name is prominent.
 */
.ph-bottom {
    padding-top: 0.3rem;
    border-bottom: 1px solid black;
    padding-bottom: 0.3rem;
    margin-bottom: 0.3rem;
    font-weight: bold;
    font-size: 0.9rem;
    color: #000;
}

/*
 * ================================================================
 *  FIXED OVERLAY
 * ================================================================
 *  The #print-header-overlay div is the "visible" header that
 *  appears on every printed page via position:fixed.
 *
 *  On SCREEN it is hidden (display:none) because the <thead>
 *  already shows the header content in normal flow.
 *
 *  On PRINT it is activated by the @media print block below.
 */
#print-header-overlay {
    display: none;
}

/*
 * ================================================================
 *  CONTENT AREA OVERRIDES
 * ================================================================
 *  These rules target the cloned app content inside #print-body
 *  (the <td> in the <tbody>).  They override app styles that
 *  would otherwise break printing.
 */

/*
 * Reset all font properties on the content area to the browser's
 * built-in defaults.  The copied app stylesheets set a variety of
 * font-family / font-size / font-weight / line-height values that
 * produce inconsistent typography when printed.  Using "revert"
 * undoes every author-level rule and falls back to the UA stylesheet.
 */
#print-body,
#print-body * {
    font-size: revert !important;
    font-weight: revert !important;
    line-height: revert !important;
    font-style: revert !important;
    letter-spacing: revert !important;
}

/*
 * Re-establish the desired base font size AFTER the revert above.
 * "revert" on font-size falls back to the UA default ("medium" / 16 px),
 * ignoring the html/body value, so changing html/body alone has no effect.
 * Setting it explicitly here — on #print-body itself, after the revert
 * block — gives all rem-based values inside the content the right root.
 */
#print-body {
    font-size: 0.9rem !important;
}

/*
 * Tone down bold elements globally.
 * The browser UA renders <th> and <strong> as font-weight:bold (700).
 * Using medium (500) looks cleaner on print without losing emphasis.
 */
#print-body th,
#print-body strong {
    font-weight: 500 !important;
}

/*
 * CRITICAL — Allow natural page breaking on ALL descendants.
 *
 * Without these overrides, two app CSS patterns prevent content
 * from being split across pages:
 *
 *   1. break-inside: avoid — Some components (KSection, form
 *      sections) use this to keep themselves on one page.  If the
 *      element is taller than the available space, Chrome forces a
 *      break anyway but miscalculates the position on the next
 *      page, causing content to overlap the repeated <thead>.
 *      Setting it to "auto" lets Chrome break naturally.
 *
 *   2. overflow: hidden — Many app components use this (KText,
 *      KCell, modals, filter widgets…).  overflow:hidden creates
 *      a Block Formatting Context (BFC), and browsers cannot
 *      split a BFC across a page boundary.  Large sections
 *      become unbreakable monoliths that overflow the page.
 *      Setting it to "visible" removes the BFC so content can
 *      flow across pages.
 *
 * page-break-inside is the legacy equivalent of break-inside,
 * included for older browser engines.
 *
 * NOTE: Table structural elements (table, thead, tbody, tfoot, tr,
 * td, th) are INTENTIONALLY excluded from the overflow override.
 * Setting overflow:visible on table cells removes the table cell
 * formatting context and can cause contained block elements to
 * lose their inherited width, breaking right-alignment and other
 * layout properties.  Page-break rules are applied to td/th
 * separately below.
 */
#print-body,
#print-body *:not(table):not(thead):not(tbody):not(tfoot):not(tr):not(td):not(th) {
    break-inside: auto !important;
    page-break-inside: auto !important;
    overflow: visible !important;
}

/* Allow page breaks inside table cells without touching overflow */
#print-body td,
#print-body th {
    break-inside: auto !important;
    page-break-inside: auto !important;
}

/* Elements marked with .print-no-break are kept on a single page. */
#print-body .print-no-break {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
}


/*
 * Hide form action bars (Save / Cancel / Sign buttons etc.)
 * that may have survived the cleanForPrint() DOM stripping.
 * Belt-and-suspenders: cleanForPrint already removes .form-actions,
 * but scoped Vue styles sometimes re-inject them.
 */
.form-actions { display: none !important; }

${fieldDisplayLayoutCss}

/*
 * Scale headings down for print.
 * The app's heading styles (from _deprecated_main.scss) are
 * designed for screen and are too large for A4.  These overrides
 * create a tighter typographic hierarchy that fits the page.
 * Sizes are smaller than the previous values so more content fits
 * per page without sacrificing readability.
 */
h1 { font-size: 1.1rem !important; }
h2 { font-size: 1.0rem !important; }
h3 { font-size: 0.9rem !important; }
h4, h5, h6 { font-size: 0.82rem !important; }

/*
 * ================================================================
 *  TABLE CELL ALIGNMENT
 * ================================================================
 *  Force top vertical alignment on all table cells in the content
 *  area.  Without this, cells with multi-line content (e.g. long
 *  text with newlines) can appear centred vertically, making
 *  rows hard to read.
 */
#print-body table td,
#print-body table th {
    vertical-align: top !important;
}


/*
 * ================================================================
 *  SCREEN-ONLY PREVIEW STYLES
 * ================================================================
 *  When the user looks at the print window before the print dialog
 *  opens, these styles give a rough A4-like preview:
 *   - padding around the body so content doesn't touch the edges
 *   - max-width: 210mm (A4 width) centres the content
 */
@media screen {
    body {
        padding: 20px;
        max-width: 210mm;
        margin: 0 auto;
    }
}

/*
 * ================================================================
 *  PRINT-ONLY OVERRIDES
 * ================================================================
 *  These rules ONLY apply when the browser enters print mode
 *  (Ctrl+P / window.print()).
 *
 *  Copied app stylesheets may set display:none, visibility:hidden,
 *  display:flex/grid etc. on body or layout elements.  The rules
 *  below forcibly restore the expected values with !important so
 *  the print document is always visible and correctly structured.
 */
@media print {
    /*
     * Ensure the document root is a visible block.
     * Some app styles hide or reformat body for screen-only UIs.
     */
    html, body {
        display: block !important;
        visibility: visible !important;
    }

    /*
     * Re-assert layout modes on header elements.
     * App stylesheets (copied for content styling) may override
     * display on generic selectors.  These rules guarantee the
     * header grid/flex layouts survive into print.
     */
    .ph-top          { display: grid !important; }
    .ph-print-info   { display: flex !important; }
    .ph-kliniken-logo { display: grid !important; }

    /*
     * Activate the fixed overlay header.
     *
     * position:fixed in Chrome's print engine places the element
     * at the same coordinates on EVERY printed page — this is
     * what makes the header repeat.
     *
     * top:0 + left:0 + right:0 + width:100% stretches it across
     * the full content area at the top of each page.
     *
     * z-index:9999 ensures it paints on top of the <thead> spacer
     * content (which has the same markup but sits in normal flow).
     *
     * background:#fff paints a solid white backdrop so that if
     * Chrome's thead-overlap bug causes tbody content to peek
     * behind the header area, it is hidden.
     */
    #print-header-overlay {
        display: block !important;
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        width: 100% !important;
        z-index: 9999 !important;
        background: #fff !important;
        margin: 0 !important;
    }
}
</style>
</head>
<body>

<!--
  COPY 1 — Fixed overlay header.
  Hidden on screen (display:none in the stylesheet above).
  On print, the @media print block activates it as position:fixed,
  which Chrome renders at the same position on every printed page.
  The white background and z-index ensure it paints cleanly on top
  of the <thead> spacer copy below.
-->
<div id="print-header-overlay" style="${headerBoxStyle}">
    ${headerHtml}
</div>

<!--
  COPY 2 — Spacer table.
  A <table> whose <thead> contains the SAME header content.
  Browsers automatically repeat <thead> at the top of every printed
  page, which pushes the <tbody> content down by exactly the header
  height + a 1.2rem gap.  Because both copies use identical markup,
  the reserved height is always correct — no JS measurement needed.

  The fixed overlay (COPY 1) paints on top of this, so the user
  only sees one clean header.  The <thead> copy is purely a spacing
  mechanism.

  All table-structure elements carry inline style="…!important" to
  prevent copied app stylesheets from overriding display properties
  (e.g. a global "table { display: block }" rule would break the
  <thead> repetition).
-->
<table style="
    width: 100% !important;             /* fill the page content area */
    border-collapse: collapse !important;/* no spacing between cells */
    table-layout: fixed !important;      /* predictable column widths */
    display: table !important;           /* override any app CSS */
    border: none !important;             /* no visible table border */
">
    <!-- thead: repeated by the browser at the top of every printed page -->
    <thead style="display: table-header-group !important;">
        <tr style="display: table-row !important;">
            <td style="
                display: table-cell !important;
                padding: 0 !important;   /* no extra space around header */
                border: none !important;
                overflow: visible !important;
            ">
                <!-- Header content (identical to the fixed overlay) -->
                <div style="${headerBoxStyle}">
                    ${headerHtml}
                </div>
                <!--
                  Gap between header and content.
                  0.8rem matches the tighter spacing used for print.
                -->
                <div style="height:0.8rem;"></div>
            </td>
        </tr>
    </thead>
    <!-- tbody: the actual page content that flows across pages -->
    <tbody style="display: table-row-group !important;">
        <tr style="display: table-row !important;">
            <td id="print-body" style="
                display: table-cell !important;
                padding: 0 !important;       /* content provides its own padding */
                border: none !important;
                vertical-align: top !important; /* start content at the top */
                overflow: visible !important; /* allow page breaks inside */
            ">
                ${contentHtml}
            </td>
        </tr>
    </tbody>
</table>

</body>
</html>`;
}

/* ------------------------------------------------------------------ */
/*  Stylesheet-ready detection                                         */
/* ------------------------------------------------------------------ */

/**
 * Returns a promise that resolves once every `<link rel="stylesheet">`
 * in the given window has a loaded `.sheet`, meaning the browser has
 * fetched and parsed it.
 *
 * This prevents the blank-print-preview problem: if `window.print()`
 * is called before stylesheets finish loading, Chrome may render the
 * print preview with unstyled (or invisible) content.
 *
 * The function polls every 80 ms and gives up after 5 seconds to avoid
 * hanging indefinitely if a stylesheet fails to load.
 *
 * @param win - The print window whose stylesheets to wait for
 */
function waitForStylesheets(win: Window): Promise<void> {
    return new Promise<void>((resolve) => {
        const MAX_WAIT = 5_000;
        const INTERVAL = 80;
        let elapsed = 0;

        function check() {
            const links = win.document.querySelectorAll('link[rel="stylesheet"]');
            let allReady = true;

            links.forEach((link) => {
                if (!(link as HTMLLinkElement).sheet) allReady = false;
            });

            if (allReady || elapsed >= MAX_WAIT) {
                resolve();
            } else {
                elapsed += INTERVAL;
                setTimeout(check, INTERVAL);
            }
        }

        check();
    });
}

/**
 * Creates a tiny off-screen iframe used as the print rendering target.
 *
 * The iframe must remain in the DOM (and not be display:none) so browser
 * print engines render its document reliably.
 */
function createPrintIframe(orientation: "portrait" | "landscape" = "portrait"): HTMLIFrameElement {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.tabIndex = -1;
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    // Set iframe dimensions to match A4 at 96 dpi so Chrome's paper-size
    // detection picks up A4 as the default in the print dialog.
    // Portrait:  210 mm × 297 mm ≈ 794 px × 1123 px
    // Landscape: 297 mm × 210 mm ≈ 1123 px × 794 px
    iframe.style.width = orientation === "landscape" ? "1123px" : "794px";
    iframe.style.height = orientation === "landscape" ? "794px" : "1123px";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    document.body.appendChild(iframe);
    return iframe;
}

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

/**
 * Opens a temporary off-screen iframe containing a print-friendly
 * version of `contentElement`, then triggers the browser's print dialog.
 *
 * ## High-level flow
 *
 * 1. **Register** the print with the backend (creates an audit log
 *    entry and returns a unique utskrifts-ID).
 * 2. **Clone** the rendered DOM of `contentElement` and strip
 *    interactive elements (buttons, textareas → static text, etc.).
 * 3. **Collect** every stylesheet from the current document so the
 *    print window's content looks identical to the main app.
 * 4. **Build** the header HTML (date, user, ID, optional patient info).
 * 5. **Assemble** a full standalone HTML document with the header
 *    (duplicated for overlay + thead spacer) and the cleaned content.
 * 6. **Create** a temporary iframe and write the document into it.
 * 7. **Wait** for all stylesheets to finish loading (prevents blank
 *    print preview).
 * 8. **Register** the `afterprint` listener (must happen *before*
 *    `print()` because `print()` blocks in most browsers).
 * 9. **Trigger** `window.print()`.
 *
 * After the user either prints or cancels, the iframe is removed.
 *
 * @param options - See {@link PrintWindowOptions}
 * @throws If the backend response is missing `utskrifts_id`
 * @throws If the browser cannot create/access the print iframe
 */
export async function printInIframe(options: PrintWindowOptions): Promise<void> {
    const {
        url,
        items,
        logName,
        contentElement,
        patientInfo,
        orientation = "portrait",
        title = "Utskrift",
        fieldDisplayLayout = "default",
    } = options;

    // 1. Register the print with the backend
    const meta = await registerPrint(url, items, logName);

    // 2. Deep-clone the rendered DOM and strip interactive elements
    const clone = contentElement.cloneNode(true) as HTMLElement;
    cleanForPrint(clone);

    // 3. Gather stylesheets (absolute URLs)
    const stylesheets = collectStylesheets();

    // 4. Build header
    const headerHtml = buildHeaderContent(meta, patientInfo);

    // 5. Assemble the full HTML document
    const html = buildDocument(headerHtml, clone.outerHTML, stylesheets, orientation, title, fieldDisplayLayout);

    // 6. Create a temporary iframe and write the document
    const iframe = createPrintIframe(orientation);
    const printWindow = iframe.contentWindow;

    if (!printWindow) {
        iframe.remove();
        throw new Error("Kunde inte skapa utskriftsfönster i dokumentet.");
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    // 7. Wait for stylesheets to finish loading
    await waitForStylesheets(printWindow);

    // Small extra delay to let the rendering engine settle
    await new Promise((r) => setTimeout(r, 250));

    // 8-9. Trigger print and remove the iframe afterwards.
    //      A timeout fallback handles browsers that skip afterprint on iframes.
    await new Promise<void>((resolve) => {
        let cleaned = false;
        const cleanup = () => {
            if (cleaned) return;
            cleaned = true;
            iframe.remove();
            resolve();
        };

        const fallbackTimer = window.setTimeout(cleanup, 60_000);

        printWindow.addEventListener(
            "afterprint",
            () => {
                window.clearTimeout(fallbackTimer);
                cleanup();
            },
            { once: true }
        );

        printWindow.focus();
        printWindow.print();
    });
}
